import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Stack,
  Grid,
  Divider,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
  Badge,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as SqftIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Tag as TagIcon,
  Description as DescriptionIcon,
  Photo as PhotoIcon,
  Build as BuildIcon,
  Person as PersonIcon,
  Apartment as ApartmentIcon,
  AccessTime as TimeIcon,
  Visibility as VisibilityIcon,
  LocalOffer as OfferIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import MarketAnalysis from './MarketAnalysis';

const statusColors = {
  Active: 'success',
  Pending: 'warning',
  Sold: 'default',
  OffMarket: 'error',
  UnderContract: 'info',
  ComingSoon: 'secondary',
  Expired: 'error',
  Withdrawn: 'default',
};

function PropertyDetail({
  property, onEdit, onBack, onDelete, onToggleWatched,
}) {
  const [activeTab, setActiveTab] = useState(0);

  if (!property) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="h6">Select a property to view details.</Typography>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatPrice = (price) => (price ? `$${parseFloat(price).toLocaleString()}` : 'Not specified');

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  const formatNumber = (value) => (value ? value.toLocaleString() : 'Not specified');

  function TabPanel({
    children, value, index, ...other
  }) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`property-tabpanel-${index}`}
        aria-labelledby={`property-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  const getPriceChange = () => {
    if (property.originalListPrice && property.price) {
      const change = ((property.price - property.originalListPrice) / property.originalListPrice) * 100;
      return change;
    }
    return null;
  };

  const priceChange = getPriceChange();

  return (
    <Paper elevation={2} sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3,
      }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {property.name || property.address}
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {property.address}
          </Typography>
          <Box sx={{
            display: 'flex', gap: 1, alignItems: 'center', mb: 2,
          }}
          >
            <Chip
              label={property.status}
              color={statusColors[property.status] || 'default'}
              size="small"
            />
            {property.mlsNumber && (
              <Chip
                label={`MLS: ${property.mlsNumber}`}
                variant="outlined"
                size="small"
              />
            )}
            {property.daysOnMarket && (
              <Chip
                label={`${property.daysOnMarket} days on market`}
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onEdit && (
            <Tooltip title="Edit Property">
              <IconButton onClick={onEdit} color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {onToggleWatched && (
            <Tooltip title={property.watched ? 'Remove from Watchlist' : 'Add to Watchlist'}>
              <IconButton onClick={() => onToggleWatched(property.id)} color="primary">
                {property.watched ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Price Section */}
      <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h3" fontWeight={700}>
                {formatPrice(property.price)}
              </Typography>
              {property.pricePerSqFt && (
                <Typography variant="h6">
                  $
                  {property.pricePerSqFt}
                  /sqft
                </Typography>
              )}
              {priceChange !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  {priceChange > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  <Typography variant="body2">
                    {priceChange > 0 ? '+' : ''}
                    {priceChange.toFixed(1)}
                    % from original price
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {property.originalListPrice && (
                <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                  Original:
                  {' '}
                  {formatPrice(property.originalListPrice)}
                </Typography>
              )}
              {property.lastSoldPrice && (
                <Typography variant="body2">
                  Last sold:
                  {' '}
                  {formatPrice(property.lastSoldPrice)}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Market Analysis" icon={<AssessmentIcon />} />
        <Tab label="Details" />
        <Tab label="Financial" />
      </Tabs>

      {/* Overview Tab */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          {/* Key Stats */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Property Specifications</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <BedIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                      <Typography variant="h6">{property.bedrooms || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary">Bedrooms</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <BathIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                      <Typography variant="h6">{property.bathrooms || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary">Bathrooms</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <SqftIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                      <Typography variant="h6">{formatNumber(property.area)}</Typography>
                      <Typography variant="body2" color="text.secondary">Sq Ft</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CalendarIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                      <Typography variant="h6">{property.yearBuilt || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary">Year Built</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Market Scores */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Market Scores</Typography>
                {property.walkScore && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Walk Score</Typography>
                      <Typography variant="body2">{property.walkScore}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={property.walkScore}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}
                {property.transitScore && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Transit Score</Typography>
                      <Typography variant="body2">{property.transitScore}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={property.transitScore}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}
                {property.bikeScore && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Bike Score</Typography>
                      <Typography variant="body2">{property.bikeScore}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={property.bikeScore}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}
                {property.schoolRating && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">School Rating</Typography>
                      <Typography variant="body2">
                        {property.schoolRating}
                        /10
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={property.schoolRating * 10}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Description */}
          {property.description && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Description</Typography>
                  <Typography variant="body1">{property.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Features */}
          {property.features && property.features.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Features</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {property.features.map((feature, index) => (
                      <Chip key={index} label={feature} variant="outlined" />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Market Analysis Tab */}
      <TabPanel value={activeTab} index={1}>
        <MarketAnalysis property={property} onRefresh={() => console.log('Refresh market data')} />
      </TabPanel>

      {/* Details Tab */}
      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Property Details</Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><HomeIcon /></ListItemIcon>
                    <ListItemText
                      primary="Property Type"
                      secondary={property.type || 'Not specified'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><LocationIcon /></ListItemIcon>
                    <ListItemText
                      primary="Address"
                      secondary={property.address || 'Not specified'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><SqftIcon /></ListItemIcon>
                    <ListItemText
                      primary="Lot Size"
                      secondary={property.lotSize ? `${property.lotSize} sqft` : 'Not specified'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CalendarIcon /></ListItemIcon>
                    <ListItemText
                      primary="Year Built"
                      secondary={property.yearBuilt || 'Not specified'}
                    />
                  </ListItem>
                  {property.neighborhood && (
                    <ListItem>
                      <ListItemIcon><LocationIcon /></ListItemIcon>
                      <ListItemText
                        primary="Neighborhood"
                        secondary={property.neighborhood}
                      />
                    </ListItem>
                  )}
                  {property.zipCode && (
                    <ListItem>
                      <ListItemIcon><LocationIcon /></ListItemIcon>
                      <ListItemText
                        primary="ZIP Code"
                        secondary={property.zipCode}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>MLS Information</Typography>
                <List>
                  {property.mlsNumber && (
                    <ListItem>
                      <ListItemIcon><BusinessIcon /></ListItemIcon>
                      <ListItemText
                        primary="MLS Number"
                        secondary={property.mlsNumber}
                      />
                    </ListItem>
                  )}
                  {property.mlsSource && (
                    <ListItem>
                      <ListItemIcon><BusinessIcon /></ListItemIcon>
                      <ListItemText
                        primary="MLS Source"
                        secondary={property.mlsSource}
                      />
                    </ListItem>
                  )}
                  {property.listDate && (
                    <ListItem>
                      <ListItemIcon><CalendarIcon /></ListItemIcon>
                      <ListItemText
                        primary="List Date"
                        secondary={formatDate(property.listDate)}
                      />
                    </ListItem>
                  )}
                  {property.lastSoldDate && (
                    <ListItem>
                      <ListItemIcon><CalendarIcon /></ListItemIcon>
                      <ListItemText
                        primary="Last Sold Date"
                        secondary={formatDate(property.lastSoldDate)}
                      />
                    </ListItem>
                  )}
                  {property.expirationDate && (
                    <ListItem>
                      <ListItemIcon><CalendarIcon /></ListItemIcon>
                      <ListItemText
                        primary="Expiration Date"
                        secondary={formatDate(property.expirationDate)}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Financial Tab */}
      <TabPanel value={activeTab} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Monthly Costs</Typography>
                <List>
                  {property.hoa && (
                    <ListItem>
                      <ListItemIcon><MoneyIcon /></ListItemIcon>
                      <ListItemText
                        primary="HOA Fees"
                        secondary={formatPrice(property.hoa)}
                      />
                    </ListItem>
                  )}
                  {property.taxes && (
                    <ListItem>
                      <ListItemIcon><MoneyIcon /></ListItemIcon>
                      <ListItemText
                        primary="Property Taxes"
                        secondary={formatPrice(property.taxes)}
                      />
                    </ListItem>
                  )}
                  {property.insurance && (
                    <ListItem>
                      <ListItemIcon><MoneyIcon /></ListItemIcon>
                      <ListItemText
                        primary="Insurance"
                        secondary={formatPrice(property.insurance)}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                <List>
                  {property.agent && (
                    <ListItem>
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText
                        primary="Agent"
                        secondary={property.agent}
                      />
                    </ListItem>
                  )}
                  {property.owner && (
                    <ListItem>
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText
                        primary="Owner"
                        secondary={property.owner}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Paper>
  );
}

export default PropertyDetail;
